import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box, Typography, Button, Paper, IconButton, Stack, Alert,
  Badge, Divider, Autocomplete, TextField, Chip, CircularProgress,
  useTheme, useMediaQuery, Avatar, Fade, SwipeableDrawer,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import ListIcon from "@mui/icons-material/List";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoIcon from "@mui/icons-material/Info";
import LabelIcon from "@mui/icons-material/Label";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { BarcodeScanner } from "src/general_components/BarcodeScanner";
import { getContenidosPaginatedDashboard } from "src/projects/gestion_documental/helpers/ContenidosData";
import { getCuentasLocalizaciones } from "src/projects/gestion_comercial/helpers/CuentasLocalizaciones";
import { getCuentasContactos } from "src/projects/gestion_comercial/cuentas/helpers/CuentasContactosData";
import { postCajasProcesosVarios } from "src/projects/gestion_documental/helpers/UbicacionCajasData";
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import esArray from "src/general_components/functionEsArray";

// ── Carga la lista de clientes desde localStorage (igual que SearchFormGeneral) ─
const cargarClientesDesdeStorage = () => {
  try {
    const cuentas = esArray(
      JSON.parse(localStorage.getItem("siam-service-getCuentasGenericas"))?.cuentas
    );
    const clientes = esArray(
      JSON.parse(localStorage.getItem("siam-cuentas_personas_juridicas"))?.cuentas_personas_juridicas
    );
    const userCuentas = esArray(JSON.parse(localStorage.getItem("siam-user-cuentas")));

    let lista = clientes.filter((c) =>
      cuentas.find((cuenta) => cuenta.id_cuentas !== c.id_cuentas)
    );

    if (userCuentas.length > 0) {
      lista = lista.filter((c) =>
        userCuentas.find((u) => u.usuarios_cuentas?.id_cuentas === c.id_cuentas)
      );
    }

    return lista.map((c) => ({ ...c, label: c.razon_social }));
  } catch {
    return [];
  }
};

// ── Lista de procesos disponibles ─────────────────────────────────────────────
const PROCESOS_LISTA = [
  { id: 2, label: "Retiro de Cajas",     color: "#003e90" },
  { id: 3, label: "Retiro Definitivo",   color: "#2e58af" },
  { id: 4, label: "Baja/Expurgue",       color: "#4c73ce" },
  { id: 5, label: "Devolucion de Cajas", color: "#ca7821" },
  { id: 6, label: "Ubicacion de Cajas",  color: "#097835" },
  { id: 7, label: "Ubicacion Temporal",  color: "#149650" },
  { id: 8, label: "Mudanza de Cajas",    color: "#1cb66c" },
];

// ── Construye el objeto datosUrl para postCajasProcesosVarios ─────────────────
const buildDatosUrl = (procesoId, proceso) => {
  const owner = JSON.parse(localStorage.getItem("clientData"))?.owner_code;
  return {
    datos: {
      valorMudanza:          procesoId === 8 ? "1" : "0",
      valorUbicacionTemporal: procesoId === 7 ? "1" : "0",
      valorRetiroCaja:       procesoId === 2 ? "1" : "0",
      valorDevolucionCaja:   procesoId === 5 ? "1" : "0",
      procesoGenerica:       [6, 7].includes(procesoId) ? "1" : "0",
      ubicacion_caja:        procesoId === 6 ? "1" : "0",
      tituloProceso:         proceso?.label || "",
      procesoSeleccionado:   proceso || null,
      ret_preferencial:      "0",
      ret_urgente:           "0",
      devolucion_caja:       procesoId === 5 ? "1" : "0",
      ret_definitivo:        procesoId === 3 ? "1" : "0",
      baja:                  procesoId === 4 ? "1" : "0",
      changeProceso:         false,
      valueSubproceso:       null,
      restricciones:         null,
      owner:                 owner,
    },
  };
};

// ── Header reutilizable ────────────────────────────────────────────────────────
const ScanHeader = ({ title, onBack, listCount, onListClick, rightAction }) => (
  <Box
    sx={{
      bgcolor: "#0A395F",
      px: 2,
      py: 1.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexShrink: 0,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      {onBack ? (
        <IconButton onClick={onBack} sx={{ color: "#fff", p: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
      ) : (
        <QrCodeScannerIcon sx={{ color: "#90caf9", fontSize: 22 }} />
      )}
      <Typography color="#fff" fontWeight={700} fontSize={16}>
        {title}
      </Typography>
    </Stack>

    <Stack direction="row" spacing={0.5} alignItems="center">
      {listCount > 0 && onListClick && (
        <Badge badgeContent={listCount} color="warning">
          <IconButton onClick={onListClick} sx={{ color: "#90caf9" }}>
            <ListIcon />
          </IconButton>
        </Badge>
      )}
      {rightAction}
    </Stack>
  </Box>
);

// ── Componente principal ───────────────────────────────────────────────────────
export const ScannerCajas = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ── Estado de escaneo ───────────────────────────────────────────────────────
  const [view, setView] = useState("inicio"); // "inicio" | "scanning" | "result" | "list"
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [locationIds, setLocationIds] = useState([]);
  const [loadingCliente, setLoadingCliente] = useState(false);

  const [cajaActual, setCajaActual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [listaEscaneada, setListaEscaneada] = useState([]);

  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const lastScanned = useRef(null);
  const cooldownRef = useRef(false);

  // ── Estado del drawer de procesos ──────────────────────────────────────────
  const [procesarOpen, setProcesarOpen] = useState(false);
  const [proceso, setProceso] = useState(null);
  const [contactoSelect, setContactoSelect] = useState(null);
  const [direccionSelect, setDireccionSelect] = useState(null);
  const [detalle, setDetalle] = useState("");
  const [contactos, setContactos] = useState([]);
  const [localizaciones, setLocalizaciones] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [depositoSelect, setDepositoSelect] = useState(null);
  const [coordenadas, setCoordenadas] = useState({
    sector: "", modulo: "", estante: "", posicion: "", profundidad: "", estibe: "",
  });
  const [procesando, setProcesando] = useState(false);

  // ── Cargar clientes al montar ──────────────────────────────────────────────
  useEffect(() => {
    const lista = cargarClientesDesdeStorage();
    setClientes(lista);
    if (lista.length === 1) {
      handleSelectCliente(lista[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Selección de cliente → obtener location IDs ────────────────────────────
  const handleSelectCliente = async (cliente) => {
    setClienteSeleccionado(cliente);
    setLocationIds([]);
    setContactos([]);
    setLocalizaciones([]);
    if (!cliente) return;

    setLoadingCliente(true);
    try {
      const locs = await getCuentasLocalizaciones(cliente.id_cuentas);
      const ids = esArray(locs).map((l) => l.id);
      setLocationIds(ids);
    } catch {
      setLocationIds([]);
    } finally {
      setLoadingCliente(false);
    }
  };

  // ── Handler de detección de barcode ───────────────────────────────────────
  const handleDetected = useCallback(async (code) => {
    if (cooldownRef.current || lastScanned.current === code) return;

    cooldownRef.current = true;
    lastScanned.current = code;
    setTimeout(() => { cooldownRef.current = false; }, 3000);

    setLoading(true);
    setErrorMsg("");

    try {
      const response = await getContenidosPaginatedDashboard(5, 0, {
        idLocalizaciones: locationIds,
        inputSearch: {
          input_general: "",
          input_numerico: "",
          input_numero_desde: "",
          input_numero_hasta: "",
          input_fecha: "",
          input_fecha_desde: "",
          input_fecha_hasta: "",
        },
        cajas: { id_cajas: code },
        soloCajas: 0,
        devuelveContenidos: false,
        busqueda_digitalizaciones: false,
      });

      if (response?.data?.length > 0) {
        setCajaActual(response.data[0]);
        setView("result");
      } else {
        setErrorMsg(`No se encontró ninguna caja con código "${code}"`);
        lastScanned.current = null;
      }
    } catch {
      setErrorMsg("Error al buscar la caja. Verificá la conexión e intentá nuevamente.");
      lastScanned.current = null;
    } finally {
      setLoading(false);
    }
  }, [locationIds]);

  const handleAgregarALista = () => {
    const yaEsta = listaEscaneada.some((c) => c.id_caja === cajaActual.id_caja);
    if (!yaEsta) {
      setListaEscaneada((prev) => [...prev, cajaActual]);
      mostrarSnack(`Caja #${cajaActual.id_caja} agregada`, "success");
    } else {
      mostrarSnack(`La caja #${cajaActual.id_caja} ya está en la lista`, "info");
    }
    irAEscanear();
  };

  const irAEscanear = () => {
    setCajaActual(null);
    setErrorMsg("");
    lastScanned.current = null;
    setView("scanning");
  };

  const irAInicio = () => {
    setCajaActual(null);
    setErrorMsg("");
    lastScanned.current = null;
    setView("inicio");
  };

  const handleRemover = (id_caja) => {
    setListaEscaneada((prev) => prev.filter((c) => c.id_caja !== id_caja));
  };

  const mostrarSnack = (msg, severity = "info") => {
    setSnackbarMsg(msg);
    setSnackbarSeverity(severity);
    setSnackbarVisible(true);
  };

  // ── Abrir drawer de procesos y precargar datos ─────────────────────────────
  const abrirProcesar = async () => {
    setProceso(null);
    setContactoSelect(null);
    setDireccionSelect(null);
    setDetalle("");
    setDepositoSelect(null);
    setCoordenadas({ sector: "", modulo: "", estante: "", posicion: "", profundidad: "", estibe: "" });
    setProcesarOpen(true);

    // Cargar contactos si no los tenemos aún
    if (clienteSeleccionado && contactos.length === 0) {
      try {
        const c = await getCuentasContactos(clienteSeleccionado.id_cuentas);
        setContactos(esArray(c));
      } catch { setContactos([]); }
    }

    // Cargar localizaciones completas (con domicilio) si no las tenemos
    if (clienteSeleccionado && localizaciones.length === 0) {
      try {
        const locs = await getCuentasLocalizaciones(clienteSeleccionado.id_cuentas);
        setLocalizaciones(esArray(locs));
      } catch { setLocalizaciones([]); }
    }

    // Depositos desde localStorage (ya cargados por FormUbicaciones en el sistema)
    const deps = esArray(
      JSON.parse(localStorage.getItem("siam-owner_localizaciones"))?.["owner_localizaciones"]
    );
    if (deps.length) setDepositos(deps);
  };

  // ── Ejecutar proceso sobre lista de cajas ─────────────────────────────────
  const handleProcesar = async () => {
    if (!proceso) return;
    setProcesando(true);

    try {
      const cajasFormateadas = listaEscaneada.map((c) => ({ id: c.id_caja }));
      const datosUrl = buildDatosUrl(proceso.id, proceso);

      // Armar datosFormUbicaciones para procesos de ubicación
      let datosFormUbicaciones = {};
      if ([6, 7, 8].includes(proceso.id)) {
        datosFormUbicaciones = {
          deposito: depositoSelect || null,
          idDep: depositoSelect?.id || "",
          disabled: Boolean(depositoSelect),
          sector:      coordenadas.sector     ? Number(coordenadas.sector)     : "",
          modulo:      coordenadas.modulo     ? Number(coordenadas.modulo)     : "",
          estante:     coordenadas.estante    ? Number(coordenadas.estante)    : "",
          posicion:    coordenadas.posicion   ? Number(coordenadas.posicion)   : "",
          estibe:      coordenadas.estibe     ? Number(coordenadas.estibe)     : "",
          profundidad: coordenadas.profundidad ? Number(coordenadas.profundidad) : "",
          estadoUbicacion: null,
          ...(proceso.id === 7 ? { ubicacion_temporal: "1" } : {}),
        };
      }

      const detalleConcat = [2, 3, 4].includes(proceso.id) ? detalle : "";

      const resultado = await postCajasProcesosVarios(
        datosFormUbicaciones,
        cajasFormateadas,
        datosUrl,
        false,            // cajasUbicadas
        "",               // ubicacionTemporal
        false,            // ajuste
        proceso.id === 4, // baja
        proceso.id === 3, // definitivo
        null,             // setModalStateUbicaciones (Swal maneja errores internamente)
        detalleConcat,
        contactoSelect,
        direccionSelect,
        null              // nodoExtra
      );

      if (resultado?.status === "true") {
        mostrarSnack(`${proceso.label} ejecutado correctamente`, "success");
        setListaEscaneada([]);
        setProcesarOpen(false);
        setView("inicio");
      }
      // En caso de error, postCajasProcesosVarios muestra Swal automáticamente
    } catch {
      mostrarSnack("Error inesperado al ejecutar el proceso.", "error");
    } finally {
      setProcesando(false);
    }
  };

  const snackbar = (
    <SnackbarMessenger
      open={snackbarVisible}
      message={snackbarMsg}
      severity={snackbarSeverity}
      hide={3000}
      close={() => setSnackbarVisible(false)}
    />
  );

  // ── Campos de coordenadas para ubicación ──────────────────────────────────
  const COORD_FIELDS = [
    ["sector", "Sector"], ["modulo", "Módulo"],
    ["estante", "Estante"], ["posicion", "Posición"],
    ["profundidad", "Profundidad"], ["estibe", "Estibe"],
  ];

  // ════════════════════════════════════════════════════════════════════════════
  // DESKTOP — aviso informativo
  // ════════════════════════════════════════════════════════════════════════════
  if (!isMobile) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          p: 4,
          textAlign: "center",
        }}
      >
        <QrCodeScannerIcon sx={{ fontSize: 80, color: "#ccc" }} />
        <Typography variant="h5" fontWeight={700}>
          Función exclusiva para mobile
        </Typography>
        <Typography variant="body1" color="text.secondary" maxWidth={400}>
          El lector de código de barras está diseñado para usarse desde un
          dispositivo móvil con cámara. Accedé desde tu celular para escanear
          etiquetas de cajas.
        </Typography>
        <Button variant="contained" href="/dashboard" sx={{ borderRadius: 3, px: 4 }}>
          Ir al Dashboard
        </Button>
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA: INICIO
  // ════════════════════════════════════════════════════════════════════════════
  if (view === "inicio") {
    return (
      <Box sx={{ height: "100dvh", display: "flex", flexDirection: "column", bgcolor: "#f0f4f8" }}>
        <ScanHeader
          title="Lector de Cajas"
          listCount={listaEscaneada.length}
          onListClick={() => setView("list")}
        />

        <Box sx={{ flex: 1, overflow: "auto", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Selector de cliente */}
          <Paper elevation={0} sx={{ borderRadius: 3, p: 2, border: "1px solid #e0e0e0" }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <PersonIcon sx={{ color: "#0A395F", fontSize: 20 }} />
              <Typography fontWeight={700} variant="body2" color="#0A395F">
                Cliente
              </Typography>
            </Stack>
            <Autocomplete
              options={clientes}
              value={clienteSeleccionado}
              onChange={(_, value) => handleSelectCliente(value)}
              getOptionLabel={(o) => `${o.id_cuentas} — ${o.razon_social}`}
              noOptionsText="Sin clientes disponibles"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Seleccioná un cliente"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCliente && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {clienteSeleccionado && !loadingCliente && (
              <Fade in>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${locationIds.length} sector(es) disponible(s)`}
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Fade>
            )}
          </Paper>

          {/* Estado de la lista */}
          {listaEscaneada.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2,
                border: "1px solid #bbdefb",
                bgcolor: "#e3f2fd",
                cursor: "pointer",
              }}
              onClick={() => setView("list")}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ bgcolor: "#1976d2", width: 32, height: 32, fontSize: 14 }}>
                    {listaEscaneada.length}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={700} color="#0d47a1">
                      Cajas en lista
                    </Typography>
                    <Typography variant="caption" color="#1565c0">
                      {listaEscaneada.map((c) => `#${c.id_caja}`).join(", ")}
                    </Typography>
                  </Box>
                </Stack>
                <ListIcon sx={{ color: "#1976d2" }} />
              </Stack>
            </Paper>
          )}

          {/* Ilustración / estado vacío */}
          {listaEscaneada.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 4,
              }}
            >
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  bgcolor: "#e3f2fd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QrCodeScannerIcon sx={{ fontSize: 48, color: "#1976d2" }} />
              </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Seleccioná un cliente y apretá{"\n"}
                <strong>Iniciar Scanner</strong> para comenzar
              </Typography>
            </Box>
          )}
        </Box>

        {/* Botón principal */}
        <Box sx={{ p: 2, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<QrCodeScannerIcon />}
            onClick={irAEscanear}
            disabled={!clienteSeleccionado || loadingCliente}
            sx={{
              borderRadius: 3,
              py: 1.75,
              fontWeight: 700,
              fontSize: 16,
              bgcolor: "#0A395F",
              "&:hover": { bgcolor: "#0d4f87" },
            }}
          >
            Iniciar Scanner
          </Button>
          {!clienteSeleccionado && (
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.75}>
              Seleccioná un cliente para continuar
            </Typography>
          )}
        </Box>

        {snackbar}
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA: SCANNER (cámara activa)
  // ════════════════════════════════════════════════════════════════════════════
  if (view === "scanning") {
    return (
      <Box sx={{ height: "100dvh", bgcolor: "#111", display: "flex", flexDirection: "column" }}>
        <ScanHeader
          title="Escaneando..."
          onBack={irAInicio}
          listCount={listaEscaneada.length}
          onListClick={() => setView("list")}
          rightAction={
            <Button
              size="small"
              startIcon={<StopIcon />}
              onClick={irAInicio}
              sx={{ color: "#ef9a9a", fontSize: 12 }}
            >
              Detener
            </Button>
          }
        />

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2, gap: 2 }}>
          <BarcodeScanner active={view === "scanning"} onDetected={handleDetected} />

          {loading && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1.5}>
              <CircularProgress size={20} sx={{ color: "#90caf9" }} />
              <Typography color="#90caf9" variant="body2" fontWeight={600}>
                Buscando caja...
              </Typography>
            </Stack>
          )}

          {errorMsg && (
            <Alert severity="warning" onClose={() => setErrorMsg("")} sx={{ borderRadius: 2 }}>
              {errorMsg}
            </Alert>
          )}

          {!loading && !errorMsg && clienteSeleccionado && (
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<PersonIcon />}
                label={clienteSeleccionado.razon_social}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#90caf9", "& .MuiChip-icon": { color: "#90caf9" } }}
              />
            </Box>
          )}

          {!loading && !errorMsg && (
            <Typography color="rgba(255,255,255,0.4)" variant="caption" textAlign="center">
              Apuntá al código de barras de la etiqueta
            </Typography>
          )}
        </Box>

        {snackbar}
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA: RESULTADO
  // ════════════════════════════════════════════════════════════════════════════
  if (view === "result" && cajaActual) {
    const yaEnLista = listaEscaneada.some((c) => c.id_caja === cajaActual.id_caja);

    const referenciaValida = cajaActual.referencia && typeof cajaActual.referencia !== "object";
    const clienteLabel = cajaActual.cliente?.replace(/\s*-\s*\(\d+\)\s*$/, "") || "—";
    const sectorLabel  = cajaActual.sector?.replace(/\s*-\s*\(\d+\)\s*$/, "")  || "—";
    const campos = [
      { icon: PersonIcon,       label: "Cliente",    value: clienteLabel },
      { icon: InfoIcon,         label: "Sector",     value: sectorLabel },
      { icon: CheckCircleIcon,  label: "Estado",     value: cajaActual.estado_caja || "—" },
      { icon: CalendarTodayIcon,label: "Ubicación",  value: cajaActual.ubicacion   || "—" },
      ...(referenciaValida ? [{ icon: LabelIcon, label: "Referencia", value: cajaActual.referencia }] : []),
    ];

    return (
      <Box sx={{ height: "100dvh", bgcolor: "#f0f4f8", display: "flex", flexDirection: "column" }}>
        <ScanHeader
          title="Caja encontrada"
          onBack={irAEscanear}
          listCount={listaEscaneada.length}
          onListClick={() => setView("list")}
        />

        {/* Banda de confirmación */}
        <Box sx={{ bgcolor: "#e8f5e9", px: 2, py: 1, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18 }} />
          <Typography color="#2e7d32" variant="caption" fontWeight={700}>
            Código detectado correctamente
          </Typography>
        </Box>

        {/* Tarjeta */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <Fade in>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
              {/* Cabecera de tarjeta */}
              <Box sx={{ bgcolor: "#0A395F", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 38, height: 38 }}>
                  <InventoryIcon sx={{ color: "#fff", fontSize: 20 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography color="rgba(255,255,255,0.65)" variant="caption">
                    ID de caja
                  </Typography>
                  <Typography color="#fff" fontWeight={800} variant="h6" lineHeight={1.2}>
                    #{cajaActual.id_caja}
                  </Typography>
                </Box>
                {yaEnLista && (
                  <Chip
                    label="En lista"
                    size="small"
                    sx={{ bgcolor: "#ffc107", color: "#000", fontWeight: 700 }}
                  />
                )}
              </Box>

              {/* Campos */}
              <Box sx={{ px: 2, pb: 1 }}>
                {campos.map(({ icon: Icon, label, value }, i) => (
                  <Box key={label}>
                    {i > 0 && <Divider />}
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
                      <Icon sx={{ fontSize: 17, color: "#0A395F", mt: 0.25, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={500} mt={0.2}>
                          {value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Fade>
        </Box>

        {/* Acciones */}
        <Box sx={{ p: 2, pt: 0, display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={yaEnLista ? <CheckCircleIcon /> : <ListIcon />}
            onClick={handleAgregarALista}
            disabled={yaEnLista}
            sx={{
              borderRadius: 3,
              py: 1.5,
              fontWeight: 700,
              fontSize: 15,
              bgcolor: "#0A395F",
              "&:hover": { bgcolor: "#0d4f87" },
            }}
          >
            {yaEnLista ? "Ya está en la lista" : "Agregar a lista"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<QrCodeScannerIcon />}
            onClick={irAEscanear}
            sx={{ borderRadius: 3, py: 1.25, fontWeight: 600, borderColor: "#0A395F", color: "#0A395F" }}
          >
            Escanear otra caja
          </Button>
          <Button
            fullWidth
            variant="text"
            size="medium"
            onClick={irAInicio}
            sx={{ borderRadius: 3, color: "text.secondary" }}
          >
            Terminar
          </Button>
        </Box>

        {snackbar}
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA: LISTA ACUMULADA
  // ════════════════════════════════════════════════════════════════════════════
  if (view === "list") {
    return (
      <Box sx={{ height: "100dvh", bgcolor: "#f0f4f8", display: "flex", flexDirection: "column" }}>
        <ScanHeader
          title={`Lista de cajas (${listaEscaneada.length})`}
          onBack={() => setView(cajaActual ? "result" : "inicio")}
          rightAction={
            listaEscaneada.length > 0 && (
              <Button
                size="small"
                sx={{ color: "#ef9a9a", fontSize: 12 }}
                onClick={() => {
                  setListaEscaneada([]);
                  mostrarSnack("Lista limpiada", "info");
                }}
              >
                Limpiar
              </Button>
            )
          }
        />

        {listaEscaneada.length === 0 ? (
          /* Estado vacío */
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InventoryIcon sx={{ fontSize: 40, color: "#90caf9" }} />
            </Box>
            <Typography color="text.secondary" textAlign="center" variant="body2">
              No hay cajas en la lista aún.{"\n"}
              Escaneá etiquetas para agregarlas.
            </Typography>
            <Button
              variant="contained"
              startIcon={<QrCodeScannerIcon />}
              onClick={irAEscanear}
              sx={{ borderRadius: 3, bgcolor: "#0A395F", "&:hover": { bgcolor: "#0d4f87" } }}
            >
              Ir a escanear
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
              <Stack spacing={1}>
                {listaEscaneada.map((caja, idx) => (
                  <Fade in key={caja.id_caja} style={{ transitionDelay: `${idx * 40}ms` }}>
                    <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
                      <Box sx={{ display: "flex", alignItems: "center", p: 1.5, gap: 1.5 }}>
                        <Avatar
                          sx={{ bgcolor: "#0A395F", width: 36, height: 36, fontSize: 13, fontWeight: 700, flexShrink: 0 }}
                        >
                          {idx + 1}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} variant="body2">
                            Caja #{caja.id_caja}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {caja.cliente?.replace(/\s*-\s*\(\d+\)\s*$/, "") || "Sin cliente"}
                          </Typography>
                          {caja.estado_caja && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {caja.estado_caja}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemover(caja.id_caja)}
                          sx={{ color: "#d32f2f", flexShrink: 0 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Fade>
                ))}
              </Stack>
            </Box>

            {/* Footer de acciones */}
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={abrirProcesar}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: 15,
                  bgcolor: "#2e7d32",
                  "&:hover": { bgcolor: "#388e3c" },
                }}
              >
                Procesar {listaEscaneada.length} caja(s)
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<QrCodeScannerIcon />}
                onClick={irAEscanear}
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                  borderColor: "#0A395F",
                  color: "#0A395F",
                }}
              >
                Seguir escaneando
              </Button>
            </Box>
          </>
        )}

        {/* ── Drawer de selección de proceso ──────────────────────────────── */}
        <SwipeableDrawer
          anchor="bottom"
          open={procesarOpen}
          onClose={() => setProcesarOpen(false)}
          onOpen={() => {}}
          disableSwipeToOpen
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: "90vh",
            },
          }}
        >
          <Box sx={{ overflowY: "auto" }}>
            {/* Drag handle */}
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
              <Box sx={{ width: 40, height: 4, bgcolor: "#ddd", borderRadius: 2 }} />
            </Box>

            <Box sx={{ px: 2, pb: 3 }}>
              {/* Título */}
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <PlayArrowIcon sx={{ color: "#2e7d32" }} />
                <Typography fontWeight={700} fontSize={16}>
                  Procesar {listaEscaneada.length} caja(s)
                </Typography>
              </Stack>

              {/* Selector de proceso */}
              <Autocomplete
                options={PROCESOS_LISTA}
                value={proceso}
                onChange={(_, val) => {
                  setProceso(val);
                  setContactoSelect(null);
                  setDireccionSelect(null);
                  setDetalle("");
                  setDepositoSelect(null);
                  setCoordenadas({ sector: "", modulo: "", estante: "", posicion: "", profundidad: "", estibe: "" });
                }}
                getOptionLabel={(o) => o.label}
                noOptionsText="Sin procesos disponibles"
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box
                      sx={{
                        width: 10, height: 10, borderRadius: "50%",
                        bgcolor: option.color, mr: 1.5, flexShrink: 0,
                      }}
                    />
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Tipo de proceso" size="small" />
                )}
                sx={{ mb: 2.5 }}
              />

              {/* ── Contacto + Dirección (Retiro, Ret.Def, Devolucion, Mudanza) ── */}
              {proceso && [2, 3, 5, 8].includes(proceso.id) && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                    Datos del retiro
                  </Typography>
                  <Autocomplete
                    options={contactos}
                    value={contactoSelect}
                    onChange={(_, val) => setContactoSelect(val)}
                    getOptionLabel={(o) => o.descripcion || ""}
                    noOptionsText="Sin contactos"
                    renderInput={(params) => (
                      <TextField {...params} label="Contacto" size="small" />
                    )}
                    sx={{ mb: 1.5 }}
                  />
                  <Autocomplete
                    options={localizaciones}
                    value={direccionSelect}
                    onChange={(_, val) => setDireccionSelect(val)}
                    getOptionLabel={(o) =>
                      [o.id, o.domicilio, o.descripcion].filter(Boolean).join(" - ")
                    }
                    noOptionsText="Sin direcciones"
                    renderInput={(params) => (
                      <TextField {...params} label="Dirección" size="small" />
                    )}
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* ── Detalle (Retiro, Ret.Def, Baja) ── */}
              {proceso && [2, 3, 4].includes(proceso.id) && (
                <TextField
                  fullWidth
                  label="Detalle (opcional)"
                  size="small"
                  value={detalle}
                  onChange={(e) => setDetalle(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              )}

              {/* ── Ubicación destino (Ubicacion, Ub.Temporal, Mudanza) ── */}
              {proceso && [6, 7, 8].includes(proceso.id) && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <LocationOnIcon sx={{ color: "#097835", fontSize: 18 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Ubicación de destino
                    </Typography>
                  </Stack>

                  <Autocomplete
                    options={depositos}
                    value={depositoSelect}
                    onChange={(_, val) => setDepositoSelect(val)}
                    getOptionLabel={(o) => o.detalle || o.descripcion || String(o.id)}
                    noOptionsText="Sin depósitos disponibles"
                    renderInput={(params) => (
                      <TextField {...params} label="Depósito" size="small" />
                    )}
                    sx={{ mb: 1.5 }}
                  />

                  {/* Coordenadas — 2 por fila */}
                  {[0, 2, 4].map((i) => (
                    <Stack key={i} direction="row" spacing={1} mb={1}>
                      {COORD_FIELDS.slice(i, i + 2).map(([key, label]) => (
                        <TextField
                          key={key}
                          fullWidth
                          label={label}
                          size="small"
                          type="number"
                          value={coordenadas[key]}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || Number(val) > 0) {
                              setCoordenadas((prev) => ({ ...prev, [key]: val }));
                            }
                          }}
                          inputProps={{ min: 1 }}
                        />
                      ))}
                    </Stack>
                  ))}
                  <Box sx={{ mb: 1.5 }} />
                </>
              )}

              {/* ── Botón ejecutar ── */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!proceso || procesando}
                onClick={handleProcesar}
                startIcon={
                  procesando
                    ? <CircularProgress size={16} color="inherit" />
                    : <PlayArrowIcon />
                }
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: 15,
                  bgcolor: proceso?.color || "#0A395F",
                  "&:hover": {
                    bgcolor: proceso?.color || "#0d4f87",
                    filter: "brightness(0.88)",
                  },
                }}
              >
                {procesando ? "Procesando..." : `Ejecutar ${proceso?.label || ""}`}
              </Button>
            </Box>
          </Box>
        </SwipeableDrawer>

        {snackbar}
      </Box>
    );
  }

  return null;
};
